import type React from 'react'
import {useEffect, useEffectEvent, useReducer, useRef} from 'react'
import type {SearchResultsType, SearchResults} from '../types/blackbird-types'
import {getExpandedQuery, parseString} from '@github-ui/blackbird-query-parsing/parsing'
import {getCustomScopesFromParam} from './use-navigate-to-query'
import {CountMode} from '@github-ui/count'
import {getBaseFetchHeaders} from '@github-ui/fetch-headers'

export interface SearchKind {
  name: SearchResultsType
  readableName: string
  readableNamePlural: string
  icon: React.FunctionComponent
}
interface ResultCount {
  mode: CountMode
  total: number
  final: boolean
  error: boolean
}

export type SearchKindCount = SearchKind & ResultCount

interface FetchKey {
  query: string
  payload: SearchResults
  searchType: SearchResultsType
}

/**
 * Atomic state for the counts hook. Consolidating `counts`, `lastSeeded`, and `isLoading`
 * into a single reducer-managed object guarantees that each transition (seed, fetchStarted,
 * fetchCompleted) updates the related fields together, removing the need to reason about
 * cross-field consistency. `isLoading` is explicit state — not derived from inputs — so that
 * input changes that don't trigger a refetch (tab switches, pagination) never flick loading
 * to `true`. This matches the original hook's behavior on `main`.
 */
interface CountsState {
  counts: Map<string, ResultCount>
  /** Tracks which payload+searchType pair we've already seeded counts from. */
  lastSeeded: {payload: SearchResults | null; searchType: SearchResultsType}
  isLoading: boolean
}

type CountsAction =
  | {type: 'seed'; payload: SearchResults; searchType: SearchResultsType}
  | {type: 'fetchStarted'}
  | {
      type: 'fetchCompleted'
      key: FetchKey
      kindsFetched: SearchKind[]
      results: Array<PromiseSettledResult<ResultCount>>
      blackbirdCount: ResultCount | null
    }

export function countsReducer(state: CountsState, action: CountsAction): CountsState {
  switch (action.type) {
    case 'seed': {
      const next = new Map(state.counts)
      next.set(action.searchType, seedFromPayload(action.searchType, action.payload))
      return {
        ...state,
        counts: next,
        lastSeeded: {payload: action.payload, searchType: action.searchType},
      }
    }
    case 'fetchStarted': {
      return state.isLoading ? state : {...state, isLoading: true}
    }
    case 'fetchCompleted': {
      const {key, kindsFetched, results, blackbirdCount} = action
      const next = new Map(state.counts)
      if (blackbirdCount) {
        // NB: Blackbird's CountRPC counts unique "documents", but we display total "files" based
        // on counting the search results. If the existing count of total files is higher, keep
        // that number to avoid confusion.
        const existing = state.counts.get(key.searchType)
        const merged: ResultCount =
          existing && existing.total > blackbirdCount.total
            ? {...blackbirdCount, total: existing.total}
            : blackbirdCount
        next.set(key.searchType, merged)
      }
      for (let i = 0; i < results.length; i++) {
        const result = results[i]
        const kind = kindsFetched[i]
        if (!result || !kind) continue
        if (result.status === 'fulfilled') {
          next.set(kind.name, {
            total: result.value.total,
            mode: result.value.mode,
            final: true,
            error: false,
          })
        } else {
          next.set(kind.name, {
            total: 0,
            mode: CountMode.Exact,
            final: true,
            error: true,
          })
        }
      }
      return {...state, counts: next, isLoading: false}
    }
  }
}

function initCountsState({
  kinds,
  searchType,
  query,
}: {
  kinds: SearchKind[]
  searchType: SearchResultsType
  query: string | null
}): CountsState {
  const counts = new Map<string, ResultCount>()
  for (const kind of kinds) {
    counts.set(kind.name, {total: 0, mode: CountMode.Exact, error: false, final: false})
  }
  // Start in loading=true iff we have a query — the effect will dispatch fetchStarted on the
  // same render's commit, but starting at true matches the original `useState(true)` so tests
  // (and any consumers) observe the correct initial value without a one-frame flicker.
  return {counts, lastSeeded: {payload: null, searchType}, isLoading: !!query}
}

function isCodeLegacyTransition(prev: SearchResultsType, curr: SearchResultsType): boolean {
  return (prev === 'code' && curr === 'codelegacy') || (prev === 'codelegacy' && curr === 'code')
}

function seedFromPayload(searchType: SearchResultsType, payload: SearchResults): ResultCount {
  if (searchType === 'code' && payload.type === 'code' && payload.metadata?.limit_reached) {
    // Set a lower bound estimate while we get the real count
    return {total: payload.result_count, mode: CountMode.LowerBound, final: false, error: false}
  }
  return {total: payload.result_count, mode: CountMode.Exact, final: true, error: false}
}

export const useSearchResultCounts = ({
  searchType,
  kinds,
  query,
  scopes,
  payload,
}: {
  searchType: SearchResultsType
  kinds: SearchKind[]
  query: string | null
  scopes: string | null
  payload: SearchResults | null
}): [boolean, SearchKindCount[], ResultCount] => {
  const [state, dispatch] = useReducer(countsReducer, {kinds, searchType, query}, initCountsState)
  const {counts, lastSeeded, isLoading} = state

  // Tracks the inputs the effect last started a fetch for. A ref is acceptable here because we
  // only read it inside the effect, never during render.
  const lastFetchedRef = useRef<FetchKey | null>(null)

  // Synchronous seed: when a new payload arrives (or we transition between `code` and `codelegacy`),
  // seed the count entry for the current search type from `payload.result_count` so the visible tab
  // never shows a stale or zero count before the async count fetch resolves. set-state-during-render
  // is the idiomatic React 19 way to derive state from changed props; the conditional ensures we
  // only fire it when state would actually change, preventing an infinite render loop.
  const needsSeed =
    !!payload && (lastSeeded.payload !== payload || isCodeLegacyTransition(lastSeeded.searchType, searchType))
  if (needsSeed) {
    dispatch({type: 'seed', payload, searchType})
  }

  const runFetchCounts = useEffectEvent(
    async ({
      query: currentQuery,
      payload: currentPayload,
      searchType: currentSearchType,
      scopes: currentScopes,
    }: {
      query: string
      payload: SearchResults
      searchType: SearchResultsType
      scopes: string | null
    }) => {
      const prev = lastFetchedRef.current
      const transition = prev ? isCodeLegacyTransition(prev.searchType, currentSearchType) : false
      // Match the original hook's skip semantics: don't refetch when either `query` or `payload`
      // is unchanged (excluding the explicit code<->codelegacy transition escape hatch). The
      // counts for tabs other than the current one were already fetched on the prior run, so
      // switching tabs alone should not trigger a network refetch. This also defends against:
      //   1. React 19 StrictMode dev-mode remounts firing twice for the same inputs.
      //   2. `scopes`-only changes when the payload instance is unchanged.
      // We deliberately do NOT dispatch anything on the skip path — `isLoading` is explicit
      // reducer state that already reflects "no fetch in flight" from the prior completion,
      // so a tab switch or pagination produces zero re-renders from this hook (matching main).
      if (prev && !transition && (prev.query === currentQuery || prev.payload === currentPayload)) {
        return
      }

      const key: FetchKey = {query: currentQuery, payload: currentPayload, searchType: currentSearchType}
      lastFetchedRef.current = key
      dispatch({type: 'fetchStarted'})

      let expandedQuery = currentQuery
      if (currentScopes) {
        const customScopes = getCustomScopesFromParam(currentScopes)
        if (customScopes.length > 0) {
          const ast = parseString(currentQuery)
          expandedQuery = getExpandedQuery(currentQuery, customScopes, ast)
        }
      }
      const kindsToFetch = kinds.filter(kind => kind.name !== currentSearchType)

      // Begin fetching counts other than the type we have via payload
      const otherResultsPromise = fetchAllCounts({
        kinds: kindsToFetch,
        query: currentQuery,
        expandedQuery,
        scopes: currentScopes || '',
        loggedIn: !!currentPayload.logged_in,
      })

      // If the blackbird query returned 100 results, follow up with a count request
      let blackbirdCount: ResultCount | null = null
      if (
        currentPayload.logged_in &&
        currentSearchType === 'code' &&
        currentPayload.type === 'code' &&
        currentPayload.metadata?.limit_reached
      ) {
        const bb = await fetchCount({
          type: 'code',
          query: currentQuery,
          expandedQuery,
          scopes: currentScopes || '',
          loggedIn: true,
        })
        if (!bb.error) blackbirdCount = bb
      }

      const results = await otherResultsPromise

      // If a newer fetch has claimed the slot since this one started, drop the dispatch.
      // Out-of-order resolution (older fetch resolves after newer) would otherwise overwrite
      // the newer fetch's results in the reducer and flip `isLoading` back to `true`.
      if (lastFetchedRef.current !== key) return

      // A single atomic transition: updates counts AND clears isLoading together.
      dispatch({type: 'fetchCompleted', key, kindsFetched: kindsToFetch, results, blackbirdCount})
    },
  )

  // get search counts from the server
  useEffect(() => {
    if (!query || !payload) {
      return
    }
    runFetchCounts({payload, scopes, query, searchType})
  }, [payload, scopes, query, searchType])

  const output: SearchKindCount[] = []
  for (const kind of kinds) {
    const count = counts.get(kind.name)
    if (count) {
      output.push({...kind, ...count})
    }
  }
  return [isLoading, output, counts.get(searchType) ?? {total: 0, mode: CountMode.Exact, final: false, error: false}]
}

function fetchAllCounts({
  kinds,
  query,
  expandedQuery,
  scopes,
  loggedIn,
}: {
  kinds: SearchKind[]
  query: string
  expandedQuery: string
  scopes: string
  loggedIn: boolean
}) {
  const mapToPromises = kinds.map(kind => fetchCount({type: kind.name, query, expandedQuery, scopes, loggedIn}))
  return Promise.allSettled(mapToPromises)
}

async function fetchCount({
  type,
  query,
  expandedQuery,
  scopes,
  loggedIn,
}: {
  type: SearchResultsType
  query: string
  expandedQuery: string
  scopes: string
  loggedIn: boolean
}): Promise<ResultCount> {
  const typeValue = type === 'codelegacy' ? 'code' : type

  if (type === 'code') {
    if (!loggedIn) {
      return {total: 0, mode: CountMode.Exact, final: true, error: false}
    }
    const url = new URL('/search/blackbird_count', window.location.href)
    url.searchParams.append('saved_searches', scopes)
    url.searchParams.append('q', query)
    const response = await fetch(url.toString(), {
      headers: {Accept: 'application/json', ...getBaseFetchHeaders()},
    })
    const json = await response.json()
    return {total: Number(json.count), mode: json.mode, final: true, error: json.failed}
  }

  if (type === 'issues') {
    expandedQuery += ' is:issue'
  } else if (type === 'pullrequests') {
    expandedQuery += ' is:pr'
  }

  const url = new URL('/search/count', window.location.href)
  url.searchParams.append('q', expandedQuery)
  url.searchParams.append('type', typeValue)

  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`${response.status}`)
  }

  const result = await response.text()
  return extractContent(type, result)
}

function extractContent(name: SearchResultsType, html: string): ResultCount {
  const el = document.createElement('html')
  // eslint-disable-next-line @github-ui/github-monorepo/require-set-sanitized-inner-html -- pre-existing innerHTML sink grandfathered when enabling the rule; not migrated in this rollout
  el.innerHTML = html
  let text = (el.textContent || '0').toLowerCase()
  let multiplier = 1
  let isLowerBound = false

  if (text.endsWith('+')) {
    isLowerBound = true
    text = text.slice(0, -1)
  }

  if (text.endsWith('k')) {
    multiplier = 1000
    text = text.slice(0, -1)
  } else if (text?.endsWith('m')) {
    multiplier = 1000000
    text = text.slice(0, -1)
  } else if (text?.endsWith('b')) {
    multiplier = 1000000000
    text = text.slice(0, -1)
  }

  return {
    mode: isLowerBound ? CountMode.LowerBound : CountMode.Exact,
    total: Number(text) * multiplier,
    final: true,
    error: false,
  }
}
